import React, { useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import { useAuth } from '../auth/AuthProvider';

const getSessionId = () => {
  try {
    const key = 'kkumhyeonlabs:analytics_session_id';
    const existing = localStorage.getItem(key);
    if (existing) return existing;
    const next = crypto.randomUUID();
    localStorage.setItem(key, next);
    return next;
  } catch {
    return `${Date.now()}_${Math.random().toString(16).slice(2)}`;
  }
};

const getClientId = () => {
  try {
    const key = 'kkumhyeonlabs:analytics_client_id';
    const existing = localStorage.getItem(key);
    if (existing) return existing;
    const next = crypto.randomUUID();
    localStorage.setItem(key, next);
    return next;
  } catch {
    return `c_${Date.now()}_${Math.random().toString(16).slice(2)}`;
  }
};

const SESSION_TTL_MS = 30 * 60 * 1000;

const ensureSession = () => {
  try {
    const idKey = 'kkumhyeonlabs:analytics_session_id';
    const tsKey = 'kkumhyeonlabs:analytics_session_last_ts';
    const now = Date.now();
    const lastTs = Number(localStorage.getItem(tsKey) || 0);
    const existing = localStorage.getItem(idKey);

    if (!existing || !lastTs || now - lastTs > SESSION_TTL_MS) {
      const next = crypto.randomUUID();
      localStorage.setItem(idKey, next);
      localStorage.setItem(tsKey, String(now));
      return { sessionId: next, isNewSession: true };
    }

    localStorage.setItem(tsKey, String(now));
    return { sessionId: existing, isNewSession: false };
  } catch {
    return { sessionId: getSessionId(), isNewSession: false };
  }
};

const getDeviceType = () => {
  try {
    const w = window.innerWidth;
    if (w < 768) return 'mobile';
    if (w < 1024) return 'tablet';
    return 'desktop';
  } catch {
    return 'unknown';
  }
};

const parseUtm = (search) => {
  try {
    const p = new URLSearchParams(search || '');
    return {
      utm_source: p.get('utm_source') || '',
      utm_medium: p.get('utm_medium') || '',
      utm_campaign: p.get('utm_campaign') || '',
    };
  } catch {
    return { utm_source: '', utm_medium: '', utm_campaign: '' };
  }
};

const AnalyticsTracker = () => {
  const location = useLocation();
  const { user } = useAuth();

  const clientId = useMemo(() => getClientId(), []);

  const lastPathRef = useRef('');
  const lastStartRef = useRef(Date.now());

  const insertEvent = async (payload) => {
    try {
      await supabase.from('ch_analytics_events').insert([
        {
          user_id: user?.id || null,
          client_id: clientId,
          session_id: payload.session_id,
          event_type: payload.event_type,
          path: payload.path || null,
          entry_path: payload.entry_path || null,
          referrer: payload.referrer || null,
          utm_source: payload.utm_source || null,
          utm_medium: payload.utm_medium || null,
          utm_campaign: payload.utm_campaign || null,
          device_type: payload.device_type || null,
          locale: payload.locale || null,
          timezone: payload.timezone || null,
          user_agent: payload.user_agent || null,
          duration_ms: Number.isFinite(payload.duration_ms) ? payload.duration_ms : null,
        },
      ]);
    } catch {
      // ignore
    }
  };

  useEffect(() => {
    const path = `${location.pathname || ''}${location.search || ''}`;
    const now = Date.now();

    const { sessionId, isNewSession } = ensureSession();

    const prevPath = lastPathRef.current;
    const prevStart = lastStartRef.current;

    const tz = (() => {
      try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
      } catch {
        return '';
      }
    })();

    const locale = (() => {
      try {
        return navigator.language || '';
      } catch {
        return '';
      }
    })();

    const base = {
      path,
      referrer: (() => {
        try {
          return document.referrer || '';
        } catch {
          return '';
        }
      })(),
      ...parseUtm(location.search),
      device_type: getDeviceType(),
      locale,
      timezone: tz,
      user_agent: (() => {
        try {
          return navigator.userAgent || '';
        } catch {
          return '';
        }
      })(),
    };

    if (prevPath) {
      const durationMs = Math.max(0, now - prevStart);
      insertEvent({
        event_type: 'page_engagement',
        session_id: sessionId,
        path: prevPath,
        duration_ms: durationMs,
        ...base,
      });
    }

    lastPathRef.current = path;
    lastStartRef.current = now;

    if (isNewSession) {
      insertEvent({
        event_type: 'session_start',
        session_id: sessionId,
        entry_path: path,
        ...base,
      });
    }

    insertEvent({
      event_type: 'page_view',
      session_id: sessionId,
      entry_path: isNewSession ? path : null,
      ...base,
    });
  }, [location.pathname, location.search]);

  useEffect(() => {
    const flush = () => {
      const prevPath = lastPathRef.current;
      const prevStart = lastStartRef.current;
      if (!prevPath || !prevStart) return;
      const durationMs = Math.max(0, Date.now() - prevStart);

      const { sessionId } = ensureSession();

      insertEvent({
        event_type: 'page_engagement',
        session_id: sessionId,
        path: prevPath,
        duration_ms: durationMs,
        referrer: '',
        ...parseUtm(''),
        device_type: getDeviceType(),
        locale: (() => {
          try {
            return navigator.language || '';
          } catch {
            return '';
          }
        })(),
        timezone: (() => {
          try {
            return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
          } catch {
            return '';
          }
        })(),
        user_agent: (() => {
          try {
            return navigator.userAgent || '';
          } catch {
            return '';
          }
        })(),
      });

      lastStartRef.current = Date.now();
    };

    const onVisibility = () => {
      if (document.visibilityState === 'hidden') flush();
    };

    window.addEventListener('beforeunload', flush);
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      window.removeEventListener('beforeunload', flush);
      document.removeEventListener('visibilitychange', onVisibility);
      flush();
    };
  }, []);

  return null;
};

export default AnalyticsTracker;
